import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Link2, Upload, X, Instagram, ExternalLink, Loader2 } from "lucide-react";
import { adminOffers, createOffer, updateOffer, deleteOffer, reorderOffers, importOffer, uploadOfferImage, mediaUrl } from "@/lib/store";

const EMPTY = { title: "", desc: "", tag: "PROMO", source_url: "", image_path: null, image: null,
  source_platform: "manual", priority: 0, starts_at: "", ends_at: "", status: "ACTIVE" };

const fileToB64 = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

function PromoForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const doImport = async () => {
    if (!f.source_url) return toast.error("Incolla prima un link Instagram/Facebook");
    setImporting(true);
    try {
      const r = await importOffer(f.source_url);
      setF((p) => ({ ...p, source_platform: r.source_platform || "instagram",
        image_path: r.image_path || p.image_path, image: r.image || p.image,
        title: p.title || r.title || "", desc: p.desc || r.desc || "" }));
      if (r.manual_upload_needed) toast.warning(r.note);
      else toast.success("Immagine importata e ottimizzata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import non riuscito");
    } finally { setImporting(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const b64 = await fileToB64(file);
      const r = await uploadOfferImage(b64);
      setF((p) => ({ ...p, image_path: r.image_path, image: r.image }));
      toast.success("Locandina caricata");
    } catch { toast.error("Caricamento non riuscito"); }
    finally { setImporting(false); }
  };

  const save = async () => {
    if (!f.title && !f.image_path && !f.image) return toast.error("Aggiungi almeno un titolo o un'immagine");
    setBusy(true);
    try {
      const body = { title: f.title, desc: f.desc, tag: f.tag, source_url: f.source_url || null,
        image_path: f.image_path || null, source_platform: f.source_platform, priority: Number(f.priority) || 0,
        starts_at: f.starts_at || null, ends_at: f.ends_at || null, status: f.status };
      if (initial?.id) await updateOffer(initial.id, body); else await createOffer(body);
      toast.success("Promozione salvata"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };

  const preview = mediaUrl(f.image);
  return (
    <div data-testid="promo-form" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-[#11141A] p-5 text-white">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold">{initial?.id ? "Modifica promozione" : "Nuova promozione"}</h3>
          <button data-testid="promo-form-close" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        <label className="mb-1 block text-xs text-zinc-400">Link Instagram / Facebook</label>
        <div className="mb-3 flex gap-2">
          <div className="flex flex-1 items-center gap-2 rounded-xl border border-white/10 bg-[#0B0E13] px-3">
            <Link2 className="h-4 w-4 text-zinc-500" />
            <input data-testid="promo-url" value={f.source_url} onChange={(e) => set("source_url", e.target.value)}
              placeholder="https://www.instagram.com/p/…" className="flex-1 bg-transparent py-2.5 text-sm outline-none placeholder:text-zinc-600" />
          </div>
          <button data-testid="promo-import-btn" onClick={doImport} disabled={importing}
            className="flex items-center gap-1.5 rounded-xl bg-[#2563EB] px-3 text-sm font-semibold disabled:opacity-50">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Instagram className="h-4 w-4" />} Importa
          </button>
        </div>

        <div className="mb-3 flex items-start gap-3">
          <div className="h-40 w-32 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-[#0B0E13]">
            {preview ? <img src={preview} alt="preview" className="h-full w-full object-cover" /> :
              <div className="flex h-full items-center justify-center text-xs text-zinc-600">Nessuna immagine</div>}
          </div>
          <div className="flex-1">
            <p className="mb-2 text-xs text-zinc-400">Il carosello usa una locandina salvata su IPHIX (WebP ottimizzato). Se l'import automatico non recupera l'immagine, caricala manualmente.</p>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="promo-file" />
            <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm text-zinc-200 hover:border-white/30">
              <Upload className="h-4 w-4" /> Carica locandina
            </button>
          </div>
        </div>

        <label className="mb-1 block text-xs text-zinc-400">Titolo</label>
        <input data-testid="promo-title" value={f.title || ""} onChange={(e) => set("title", e.target.value)}
          className="mb-3 w-full rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm outline-none" />

        <label className="mb-1 block text-xs text-zinc-400">Descrizione (opzionale)</label>
        <textarea data-testid="promo-desc" value={f.desc || ""} onChange={(e) => set("desc", e.target.value)} rows={2}
          className="mb-3 w-full resize-none rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm outline-none" />

        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-zinc-400">Etichetta</label>
            <input data-testid="promo-tag" value={f.tag || ""} onChange={(e) => set("tag", e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-zinc-400">Priorità (0 = più alta)</label>
            <input data-testid="promo-priority" type="number" value={f.priority ?? 0} onChange={(e) => set("priority", e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-zinc-400">Inizio</label>
            <input data-testid="promo-start" type="datetime-local" value={f.starts_at ? f.starts_at.slice(0, 16) : ""}
              onChange={(e) => set("starts_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
              className="w-full rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm outline-none" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-zinc-400">Scadenza</label>
            <input data-testid="promo-end" type="datetime-local" value={f.ends_at ? f.ends_at.slice(0, 16) : ""}
              onChange={(e) => set("ends_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
              className="w-full rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm outline-none" />
          </div>
        </div>

        <label className="mb-3 flex items-center gap-2 text-sm">
          <input data-testid="promo-status" type="checkbox" checked={f.status === "ACTIVE"}
            onChange={(e) => set("status", e.target.checked ? "ACTIVE" : "INACTIVE")} />
          Promozione attiva
        </label>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-xl border border-white/10 px-4 py-2 text-sm text-zinc-300">Annulla</button>
          <button data-testid="promo-save" onClick={save} disabled={busy}
            className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Promozioni() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const drag = useRef(null);

  const load = () => adminOffers().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);

  const now = new Date().toISOString();
  const statusLabel = (o) => {
    if (o.is_deleted) return null;
    if (o.status === "INACTIVE") return { t: "Disattivata", c: "bg-zinc-500/15 text-zinc-400" };
    if (o.ends_at && now > o.ends_at) return { t: "Scaduta", c: "bg-rose-500/15 text-rose-400" };
    if (o.starts_at && now < o.starts_at) return { t: "Programmata", c: "bg-amber-500/15 text-amber-400" };
    return { t: "Attiva", c: "bg-emerald-500/15 text-emerald-400" };
  };

  const onDrop = async (i) => {
    if (drag.current == null || drag.current === i) return;
    const arr = [...rows]; const [m] = arr.splice(drag.current, 1); arr.splice(i, 0, m);
    drag.current = null; setRows(arr);
    try { await reorderOffers(arr.map((r) => r.id)); toast.success("Ordine aggiornato"); } catch { load(); }
  };

  const remove = async (id) => { await deleteOffer(id); toast.success("Promozione eliminata"); load(); };
  const toggle = async (o) => { await updateOffer(o.id, { status: o.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" }); load(); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-500">Social · Promozioni</h3>
          <p className="mt-1 text-sm text-zinc-400">Incolla un link Instagram/Facebook o carica una locandina. Trascina per riordinare.</p>
        </div>
        <button data-testid="promo-add-btn" onClick={() => { setEditing(null); setOpen(true); }}
          className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white">
          <Plus className="h-4 w-4" /> Aggiungi promozione
        </button>
      </div>

      {rows.length === 0 && <p className="py-8 text-center text-sm text-zinc-500">Nessuna promozione. Aggiungine una per popolare il carosello della home.</p>}

      <div className="space-y-2">
        {rows.map((o, i) => {
          const st = statusLabel(o);
          const img = mediaUrl(o.image);
          return (
            <div key={o.id} data-testid={`promo-row-${o.id}`} draggable
              onDragStart={() => (drag.current = i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
              className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#11141A] p-3">
              <GripVertical className="h-5 w-5 shrink-0 cursor-grab text-zinc-600" />
              <div className="h-16 w-14 shrink-0 overflow-hidden rounded-lg bg-[#0B0E13]">
                {img ? <img src={img} alt="" className="h-full w-full object-cover" /> :
                  <div className="flex h-full items-center justify-center text-[10px] text-zinc-600">no img</div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-semibold text-white">{o.title || "(senza titolo)"}</span>
                  {st && <span className={`rounded-full px-2 py-0.5 text-[10px] ${st.c}`}>{st.t}</span>}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-zinc-500">
                  <span>Priorità {o.priority ?? 0}</span>
                  {o.source_url && <a href={o.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#2563EB]"><ExternalLink className="h-3 w-3" /> link</a>}
                </div>
              </div>
              <button data-testid={`promo-toggle-${o.id}`} onClick={() => toggle(o)}
                className={`rounded-lg px-2.5 py-1.5 text-xs ${o.status === "ACTIVE" ? "border border-emerald-500/30 text-emerald-400" : "border border-white/10 text-zinc-400"}`}>
                {o.status === "ACTIVE" ? "ON" : "OFF"}
              </button>
              <button data-testid={`promo-edit-${o.id}`} onClick={() => { setEditing(o); setOpen(true); }}
                className="rounded-lg border border-white/10 px-2.5 py-1.5 text-xs text-zinc-200">Modifica</button>
              <button data-testid={`promo-delete-${o.id}`} onClick={() => remove(o.id)}
                className="rounded-lg border border-white/10 px-2 py-1.5 text-rose-400 hover:border-rose-500/40"><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
      </div>

      {open && <PromoForm initial={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}
